import { useQuery } from '@tanstack/react-query'
import { getDefaultChoices } from '../api/master'

/** The company's accounts of some kinds ('Cash,Card', 'AR' or 'AP'):
    [{code, name, label, kind, kind_label, main, store}], main ones first. */
export function useDefaultChoices(kinds, use) {
  const { data } = useQuery({ queryKey: ['default-choices', kinds, use || ''], queryFn: () => getDefaultChoices(kinds, undefined, use), staleTime: 60_000 })
  return Array.isArray(data) ? data : null
}

/** The account used when none is picked: on a payment, the one Transaction
    accounts gives that payment for the method (auto); else for money, cash
    to the main Cash account and anything else to the main bank one;
    otherwise the main one. */
export const defaultFor = (accounts, method) => {
  const how = String(method || '').toLowerCase() === 'cash' ? 'cash' : 'other'
  const auto = accounts.find((a) => (a.auto || []).includes(how))
  if (auto) return auto
  const money = accounts.some((a) => a.kind === 'Cash' || a.kind === 'Card')
  const kind = money ? (how === 'cash' ? 'Cash' : 'Card') : accounts[0]?.kind
  return accounts.find((a) => a.kind === kind && a.main) || accounts.find((a) => a.kind === kind) || accounts[0]
}

const optionText = (a) => `${a.code} · ${a.name}${a.label && a.label.toLowerCase() !== String(a.name).toLowerCase() ? ` (${a.label})` : ''}`

// Pick one of the company's accounts of a kind: cash and bank on payments,
// receivables on invoices, payables on bills. Blank means the default (by
// payment method, for money); the server applies the same rule.
export default function DefaultAccountSelect({ kinds = 'Cash,Card', use, accounts: given, method, value, onChange, ...rest }) {
  const loaded = useDefaultChoices(kinds, use)
  const all = given || loaded || []
  const auto = defaultFor(all, method)
  // For money the blank choice depends on the method, so every account is
  // listed; otherwise blank is the main account, listed once.
  const accounts = method ? all : all.filter((a) => a !== auto)
  const groups = [...new Set(accounts.map((a) => a.kind_label))]
  const why = method ? `for ${String(method).toLowerCase()}` : 'main'
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} {...rest}>
      <option value="">{auto ? `${auto.code} · ${auto.name} (${why})` : 'Automatic'}</option>
      {value && !all.some((a) => a.code === value) && <option value={value}>{value}</option>}
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {accounts.filter((a) => a.kind_label === g).map((a) => <option key={a.code} value={a.code}>{optionText(a)}{a.main ? ' (main)' : ''}</option>)}
        </optgroup>
      ))}
    </select>
  )
}
